<template>
  <div>
    <v-dialog :value="value != null" persistent max-width="1100px">
      <v-card style="padding: 0px">
        <v-card-title>
          Select Route
          <v-spacer></v-spacer>
          <v-btn icon @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <div class="caption mb-3" v-if="value">
            {{ cityStart }} → {{ cityEnd }}
          </div>

          <v-row class="mb-2" align="center">
            <v-col cols="4" class="py-1">
              <f-text v-model="labelFilter" label="Filter label" smlabel />
            </v-col>
            <v-col cols="3" class="py-1">
              <f-text v-model="viaFilter" label="Filter vias" smlabel />
            </v-col>
            <v-col cols="3" class="py-1">
              <f-text v-model="descriptionFilter" label="Filter description" smlabel />
            </v-col>
            <v-col cols="2" class="py-1 d-flex justify-end">
              <g-button label="Add route" icon="add" @onclick="openCreateDialog" />
            </v-col>
          </v-row>

          <div class="overflow-y-auto" style="max-height: 480px">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th style="width: 40px"></th>
                  <th>Label</th>
                  <th>Distance</th>
                  <th>Travel time</th>
                  <th>Vias</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="route in filteredRoutes"
                  :key="route.id"
                  class="route-row"
                  :class="{ 'selected-route': selectedRouteId === route.id }"
                  @click="selectedRouteId = route.id"
                >
                  <td>
                    <v-icon small v-if="selectedRouteId === route.id">mdi-radiobox-marked</v-icon>
                    <v-icon small v-else>mdi-radiobox-blank</v-icon>
                  </td>
                  <td>{{ formatLabel(route) }}</td>
                  <td>{{ formatDistance(route.distance) }}</td>
                  <td>{{ route.travel_time || "—" }}</td>
                  <td>{{ formatVias(route.vias) }}</td>
                  <td>
                    <div class="description-cell">{{ route.description || "—" }}</div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="!loading && filteredRoutes.length === 0" class="text-center py-8 grey--text">
              No routes available for these cities.
            </div>
            <div v-if="loading" class="text-center py-8 grey--text">Loading routes…</div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <g-button label="Cancel" @onclick="close" />
          <v-spacer></v-spacer>
          <g-button label="Select" type="primary" :disabled="!selectedRouteId" @onclick="select" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <RouteCreateDialog
      v-model="createDialog"
      :cityStart="cityStart"
      :cityEnd="cityEnd"
      @created="onRouteCreated"
    />
  </div>
</template>

<script>
import { formatRouteVias, formatRouteLabel, sortRoutesForPicker, normalizeRouteVias, normalizeRouteCity, RouteCreateDialog } from "@/modules/routes";

export default {
  components: {
    RouteCreateDialog,
  },
  props: {
    value: Object,
  },
  data() {
    return {
      routes: [],
      selectedRouteId: null,
      labelFilter: "",
      viaFilter: "",
      descriptionFilter: "",
      loading: false,
      createDialog: false,
    };
  },
  computed: {
    cityStart() {
      return normalizeRouteCity(this.value && this.value.cityStart);
    },
    cityEnd() {
      return normalizeRouteCity(this.value && this.value.cityEnd);
    },
    filteredRoutes() {
      const labelNeedle = (this.labelFilter || "").trim().toLowerCase();
      const viaNeedle = (this.viaFilter || "").trim().toLowerCase();
      const descriptionNeedle = (this.descriptionFilter || "").trim().toLowerCase();

      return sortRoutesForPicker(this.routes).filter((route) => {
        const labelText = formatRouteLabel(route).toLowerCase();
        const viasText = formatRouteVias(route.vias).toLowerCase();
        const descriptionText = String(route.description || "").toLowerCase();
        if (labelNeedle && !labelText.includes(labelNeedle)) return false;
        if (viaNeedle && !viasText.includes(viaNeedle)) return false;
        if (descriptionNeedle && !descriptionText.includes(descriptionNeedle)) return false;
        return true;
      });
    },
  },
  methods: {
    formatVias(vias) {
      return formatRouteVias(vias);
    },
    formatLabel(route) {
      return formatRouteLabel(route);
    },
    formatDistance(distance) {
      if (distance == null || distance === "") return "—";
      return `${distance} km`;
    },
    citiesMatch(leftStart, leftEnd, rightStart, rightEnd) {
      return (
        normalizeRouteCity(leftStart).toLowerCase() === normalizeRouteCity(rightStart).toLowerCase() &&
        normalizeRouteCity(leftEnd).toLowerCase() === normalizeRouteCity(rightEnd).toLowerCase()
      );
    },
    close() {
      this.createDialog = false;
      this.$emit("input", null);
    },
    openCreateDialog() {
      this.createDialog = true;
    },
    onRouteCreated(created) {
      if (!created || !created.id) return;
      const matchesCities = this.value && this.citiesMatch(created.city_start, created.city_end, this.cityStart, this.cityEnd);

      if (matchesCities) {
        const route = {
          ...created,
          vias: normalizeRouteVias(created.vias),
        };
        const alreadyListed = this.routes.some((item) => item.id === route.id);
        this.routes = alreadyListed ? this.routes : [...this.routes, route];
        this.selectedRouteId = route.id;
      } else if (this.value) {
        this.fetchRoutesByCities(this.cityStart, this.cityEnd).then(() => {
          this.selectedRouteId = created.id;
        });
      }
    },
    select() {
      if (!this.selectedRouteId) return;
      const selectedRoute = this.routes.find((route) => route.id === this.selectedRouteId);
      if (!selectedRoute) return;
      this.$emit("select", selectedRoute);
      this.close();
    },
    fetchRoutesByCities(cityStart, cityEnd) {
      const start = normalizeRouteCity(cityStart);
      const end = normalizeRouteCity(cityEnd);
      if (!start || !end) {
        this.routes = [];
        this.selectedRouteId = null;
        return Promise.resolve([]);
      }

      this.loading = true;
      return this.$store
        .dispatch("RouteStore/getRoutesByCities", { cityStart: start, cityEnd: end })
        .then((routes) => {
          this.routes = (routes || []).map((route) => ({
            ...route,
            vias: normalizeRouteVias(route.vias),
          }));
          this.selectedRouteId = this.value && this.value.routeId ? this.value.routeId : null;
        })
        .catch((err) => {
          this.routes = [];
          this.$root.$error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    value: {
      handler(val) {
        this.viaFilter = "";
        this.descriptionFilter = "";
        this.labelFilter = "";
        this.createDialog = false;
        if (val) {
          this.fetchRoutesByCities(val.cityStart, val.cityEnd);
        } else {
          this.routes = [];
          this.selectedRouteId = null;
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.route-row {
  cursor: pointer;
}
.route-row:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
.selected-route {
  background-color: rgba(0, 0, 0, 0.06);
}
.description-cell {
  white-space: pre-wrap;
  max-height: 4.5em;
  overflow: hidden;
}
</style>
